import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import ProductPage from "@/pages/ProductPage";
import Cart from "@/pages/Cart";
import Success from "@/pages/Success";
import FAQ from "@/pages/FAQ";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Returns from "@/pages/Returns";
import Wholesale from "@/pages/Wholesale";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Account from "@/pages/Account";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AffiliateApply from "@/pages/AffiliateApply";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RefTracker from "@/components/RefTracker";
import { AuthProvider } from "@/contexts/AuthContext";
import { CustomerProtected, AdminProtected } from "@/components/ProtectedRoute";

export const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem("stageletics_cart");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("stageletics_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (sku, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.sku === sku);
      if (existing) {
        return prev.map((i) =>
          i.sku === sku ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [...prev, { sku, quantity }];
    });
  };

  const updateQty = (sku, quantity) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.sku !== sku));
    } else {
      setItems((prev) =>
        prev.map((i) => (i.sku === sku ? { ...i, quantity } : i))
      );
    }
  };

  const removeItem = (sku) =>
    setItems((prev) => prev.filter((i) => i.sku !== sku));

  const clearCart = () => setItems([]);

  const replaceCart = (nextItems) => {
    // Filter to valid shape: {sku, quantity}
    const cleaned = (nextItems || [])
      .filter((i) => i && i.sku && Number.isFinite(Number(i.quantity)) && Number(i.quantity) > 0)
      .map((i) => ({ sku: i.sku, quantity: Number(i.quantity) }));
    setItems(cleaned);
  };

  const count = items.reduce((acc, i) => acc + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clearCart, count, replaceCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <RefTracker />
            <Navbar />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/product" element={<ProductPage />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/success" element={<Success />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/returns" element={<Returns />} />
              <Route path="/wholesale" element={<Wholesale />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account" element={<CustomerProtected><Account /></CustomerProtected>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminProtected><AdminDashboard /></AdminProtected>} />
              <Route path="/affiliate" element={<AffiliateApply />} />
              <Route path="/affiliate/dashboard" element={<CustomerProtected><AffiliateDashboard /></CustomerProtected>} />
            </Routes>
            <Footer />
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#0F0F13",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                },
              }}
            />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
